import React, { useState } from 'react';
import { Container, Form } from 'react-bootstrap';
import { useLocation, useHistory } from 'react-router-dom';
import { get } from 'lodash';
import { joinRoom, getRoom, createRoom, getRoomStatus, leaveRoom } from '../lib/endpoints';
import { DEFAULT_CONFIG } from '../lib/store';
import {
  loadAuth,
  loadAuthForRoom,
  clearAuth,
  normalizeName,
  namesEqual,
} from '../lib/sessionAuth';
import Header from '../components/Header';
import Footer, { FooterSimple } from '../components/Footer';

const ERROR_TYPE = {
  emptyCode: 'emptyCode',
  roomCode: 'roomCode',
  name: 'name',
  hostRoom: 'hostRoom',
  fullRoom: 'fullRoom',
  dupName: 'dupName',
  scores: 'scores',
  gameStarted: 'gameStarted',
};

const ERROR_MESSAGE = {
  [ERROR_TYPE.emptyCode]: 'Xona kodini kiriting',
  [ERROR_TYPE.roomCode]: 'Bu kod bilan xona topilmadi',
  [ERROR_TYPE.name]: 'Jamoa nomini kiriting',
  [ERROR_TYPE.dupName]:
    'Bu jamoa nomi band — boshqa nom yozing',
  [ERROR_TYPE.hostRoom]: 'Xona yaratib bo‘lmadi, qayta urinib ko‘ring',
  [ERROR_TYPE.fullRoom]: 'Xona to‘ldi',
  [ERROR_TYPE.scores]: 'Ball va jarima 1 dan katta bo‘lishi kerak',
  [ERROR_TYPE.gameStarted]:
    'O‘yin boshlangan. Shu qurilmadan qayta kiring yoki yangi xona oching',
};

const HOST_CONFIG_KEY = 'zakbuzz_host_config';

export default function Lobby({ setAuth, resetAuth }) {
  const location = useLocation();
  const prefilledRoomID = get(location, 'state.roomID');
  const prefilledName = get(location, 'state.name', '');

  const history = useHistory();
  const [name, setName] = useState(() => {
    if (prefilledName) return prefilledName;
    const saved = prefilledRoomID
      ? loadAuthForRoom(prefilledRoomID)
      : null;
    return (saved && saved.name) || '';
  });
  const [room, setRoom] = useState(prefilledRoomID || '');
  const [joinMode, setJoinMode] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [startingScore, setStartingScore] = useState(
    String(DEFAULT_CONFIG.startingScore)
  );
  const [penalty, setPenalty] = useState(String(DEFAULT_CONFIG.penalty));

  async function enterRoom(roomId, hosting = false, hostConfig = null) {
    if (!hosting) {
      setLoading(true);
    }

    try {
      const roomRes = await getRoom(roomId);
      if (roomRes.status !== 200) {
        throw new Error(ERROR_TYPE.roomCode);
      }
      const roomData = roomRes.data;
      const roomID = String(roomData.roomID).toUpperCase();
      const players = roomData.players || [];

      // Boshqa xona sessiyasi aralashmasin
      const previous = loadAuth();
      if (previous && previous.roomID !== roomID) {
        try {
          await leaveRoom(
            previous.roomID,
            previous.playerID,
            previous.credentials
          );
        } catch (e) {
          // ignore
        }
        clearAuth();
      }

      // Asosiy yo‘l: localStorage’dagi roomID + playerID + credentials
      const saved = !hosting ? loadAuthForRoom(roomID) : null;
      if (saved) {
        const seat = players.find(
          (p) => String(p.id) === String(saved.playerID)
        );
        if (seat) {
          const seatName = seat.name || saved.name || 'Jamoa';
          if (!seat.connected) {
            const joinRes = await joinRoom(roomID, saved.playerID, seatName);
            if (joinRes.status === 200 && joinRes.data) {
              setAuth({
                playerID: saved.playerID,
                credentials: joinRes.data.playerCredentials,
                roomID,
                name: seatName,
                hostConfig: null,
              });
              setLoading(false);
              history.push(`/${roomID}`);
              return;
            }
          } else {
            // Shu qurilma / sessiya — saqlangan credentials bilan qayta ulanish
            setAuth({
              ...saved,
              roomID,
              name: seatName,
              hostConfig: null,
            });
            setLoading(false);
            history.push(`/${roomID}`);
            return;
          }
        } else {
          // Joy yo‘qolgan — eski sessiya yaroqsiz
          clearAuth();
        }
      }

      // Birinchi marta kirish — jamoa nomi kerak
      const trimmedName = normalizeName(name);
      if (!trimmedName) {
        throw new Error(ERROR_TYPE.name);
      }

      // Nom band: faqat localStorage orqali qayta kirish mumkin (yuqorida)
      const playerSeat = players.find(
        (player) => player.name && namesEqual(player.name, trimmedName)
      );
      if (playerSeat) {
        throw new Error(ERROR_TYPE.dupName);
      }

      const freeSeat = players.find((player) => !player.name);

      if (!hosting) {
        const statusRes = await getRoomStatus(roomID);
        const phase =
          statusRes.status === 200 && statusRes.data
            ? statusRes.data.phase
            : 'lobby';
        if (phase === 'playing' || phase === 'finished') {
          throw new Error(ERROR_TYPE.gameStarted);
        }
      }

      if (!freeSeat) {
        throw new Error(ERROR_TYPE.fullRoom);
      }

      const seatName = trimmedName;
      const playerID = get(freeSeat, 'id');
      const joinRes = await joinRoom(roomID, playerID, seatName);
      if (joinRes.status !== 200) {
        throw new Error(ERROR_TYPE.roomCode);
      }
      const creds = joinRes.data;

      if (hosting && hostConfig) {
        try {
          sessionStorage.setItem(
            `${HOST_CONFIG_KEY}_${roomID}`,
            JSON.stringify(hostConfig)
          );
        } catch (e) {
          // ignore
        }
      }

      setAuth({
        playerID,
        credentials: creds.playerCredentials,
        roomID,
        name: seatName,
        hostConfig: hosting ? hostConfig : null,
      });
      setLoading(false);
      history.push(`/${roomID}`);
    } catch (err) {
      setLoading(false);
      setError(ERROR_MESSAGE[err.message] || 'Xatolik yuz berdi');
    }
  }

  async function makeRoom() {
    const scoreNum = Number(startingScore);
    const penaltyNum = Number(penalty);
    if (scoreNum < 0 || Number.isNaN(scoreNum) || !penaltyNum || penaltyNum < 1) {
      setError(ERROR_MESSAGE[ERROR_TYPE.scores]);
      return;
    }

    setLoading(true);
    try {
      // New room: drop any previous session completely
      const previous = loadAuth();
      if (previous) {
        try {
          await leaveRoom(
            previous.roomID,
            previous.playerID,
            previous.credentials
          );
        } catch (e) {
          // ignore
        }
        clearAuth();
      }

      const createRes = await createRoom();
      if (createRes.status !== 200) {
        throw new Error(ERROR_TYPE.hostRoom);
      }
      const roomID = createRes.data.gameID;
      await enterRoom(roomID, true, {
        startingScore: scoreNum,
        penalty: penaltyNum,
      });
    } catch (err) {
      setLoading(false);
      setError(ERROR_MESSAGE[err.message] || 'Xatolik yuz berdi');
    }
  }

  function handleSubmit(event) {
    event.preventDefault();

    if (joinMode) {
      const code = room.trim().toUpperCase();
      if (code.length === 0) {
        setError(ERROR_MESSAGE[ERROR_TYPE.emptyCode]);
      } else if (code.length !== 6) {
        setError(ERROR_MESSAGE[ERROR_TYPE.roomCode]);
      } else if (!loadAuthForRoom(code) && normalizeName(name).length === 0) {
        setError(ERROR_MESSAGE[ERROR_TYPE.name]);
      } else {
        enterRoom(code);
      }
    } else {
      if (normalizeName(name).length === 0) {
        setError(ERROR_MESSAGE[ERROR_TYPE.name]);
      } else {
        makeRoom();
      }
    }
  }

  const savedSession =
    room.trim().length === 6 ? loadAuthForRoom(room.trim()) : null;

  const form = joinMode ? (
    <Form className="lobby-form card-panel" onSubmit={(e) => handleSubmit(e)}>
      <p className="eyebrow">Menbirinchi</p>
      <h3>Xonaga qo‘shilish</h3>
      <Form.Group controlId="room">
        <Form.Label>Xona kodi</Form.Label>
        <Form.Control
          value={room}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="characters"
          spellCheck="false"
          placeholder="ABC123"
          onChange={(e) => {
            setError('');
            setRoom(e.target.value);
          }}
        />
      </Form.Group>

      {savedSession ? (
        <p className="hint soft">
          Saqlangan sessiya: <strong>{savedSession.name || 'Jamoa'}</strong>
        </p>
      ) : (
        <Form.Group controlId="name">
          <Form.Label>Jamoa nomi</Form.Label>
          <Form.Control
            value={name}
            placeholder="Jamoa nomi"
            onChange={(e) => {
              setError('');
              setName(e.target.value);
            }}
          />
        </Form.Group>
      )}

      <div className="error-message">{error}</div>
      <button type="submit" className="btn-solid" disabled={loading}>
        {loading
          ? 'Kirilmoqda...'
          : savedSession
            ? 'O‘yinga qaytish'
            : 'Kirish'}
      </button>
      <div className="switcher">
        Boshlovchimisiz?{' '}
        <button
          type="button"
          className="inline"
          onClick={() => {
            setError('');
            setJoinMode(false);
          }}
        >
          Xona yaratish
        </button>
      </div>
    </Form>
  ) : (
    <Form className="lobby-form card-panel" onSubmit={(e) => handleSubmit(e)}>
      <p className="eyebrow">Boshlovchi</p>
      <h3>Xona yaratish</h3>
      <Form.Group controlId="name">
        <Form.Label>Boshlovchi nomi</Form.Label>
        <Form.Control
          value={name}
          placeholder="Ismingiz"
          onChange={(e) => {
            setError('');
            setName(e.target.value);
          }}
        />
      </Form.Group>

      <Form.Group controlId="startingScore">
        <Form.Label>Jamoa boshlang‘ich bali</Form.Label>
        <Form.Control
          type="number"
          min="0"
          value={startingScore}
          onChange={(e) => {
            setError('');
            setStartingScore(e.target.value);
          }}
        />
      </Form.Group>

      <Form.Group controlId="penalty">
        <Form.Label>Noto‘g‘ri javob jarimasi</Form.Label>
        <Form.Control
          type="number"
          min="1"
          value={penalty}
          onChange={(e) => {
            setError('');
            setPenalty(e.target.value);
          }}
        />
      </Form.Group>

      <div className="error-message">{error}</div>
      <button type="submit" className="btn-solid" disabled={loading}>
        {loading ? 'Yaratilmoqda...' : 'Xonani yaratish'}
      </button>
      <div className="switcher">
        Jamoamisiz?{' '}
        <button
          type="button"
          className="inline"
          onClick={() => {
            setError('');
            setJoinMode(true);
          }}
        >
          Xonaga kirish
        </button>
      </div>
    </Form>
  );

  const touts = (
    <div className="touts">
      <div className="feature-card">
        <h4>Bitta boshlovchi</h4>
        <p>Ovoz, ball va savol narxi — hammasi bitta paneldan</p>
      </div>
      <div className="feature-card teal">
        <h4>Avto restart</h4>
        <p>Har yangi savolda navbat o‘zi tozalanadi</p>
      </div>
      <div className="feature-card orange">
        <h4>Jonli reyting</h4>
        <p>10–15 jamoa, telefon yoki noutbukdan</p>
      </div>
    </div>
  );

  return (
    <main id="lobby">
      <section className="primary d-none d-md-flex">
        <div id="lobby-left">
          <div>
            <Header />
            <section className="container-half">
              <p className="hero-kicker">Zakovat kechasi</p>
              <h1 className="hero-title">Menbirinchi</h1>
              <p className="hero-sub">
                Navbat, ball va savol narxi — zamonaviy bitta platforma
              </p>
              {touts}
            </section>
          </div>
          <section className="container-half">
            <FooterSimple />
          </section>
        </div>
        <div id="lobby-right">
          <section className="container-half">{form}</section>
        </div>
      </section>
      <section className="primary d-block d-md-none">
        <Header />
        <Container className="container-mobile">
          <h1 className="hero-title mobile">Menbirinchi</h1>
          {form}
        </Container>
        <div className="divider" />
        <Container className="container-mobile">{touts}</Container>
      </section>
      <Footer mobileOnly />
    </main>
  );
}
